import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { isAuthenticated, logout } from "./auth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("token");
      console.log("Initializing auth, isAuthenticated:", isAuthenticated(), "Stored Token:", storedToken ? "exists" : "null");

      if (isAuthenticated() && storedToken) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}auth/user/profile`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          console.log("User profile fetched:", response.data);
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          console.error("Profile fetch failed:", error.response?.data?.detail || error.message);
          if (!isLoggingOut) {
            setIsLoggingOut(true);
            logout();
            setToken(null);
            setUser(null);
            setIsLoggingOut(false);
          }
        }
      } else {
        console.log("No valid token found, clearing state");
        if (!isLoggingOut) {
          setIsLoggingOut(true);
          logout();
          setUser(null);
          setToken(null);
          setIsLoggingOut(false);
        }
      }
      setLoading(false);
    };
    initializeAuth();
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated() && !token && !isLoggingOut) {
      console.log("Detected token in localStorage after initialization, syncing state");
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        const syncAuth = async () => {
          try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}auth/user/profile`, {
              headers: { Authorization: `Bearer ${storedToken}` },
            });
            console.log("Synced user profile:", response.data);
            setUser(response.data);
            setToken(storedToken);
          } catch (error) {
            console.error("Sync profile fetch failed:", error.response?.data?.detail || error.message);
            if (!isLoggingOut) {
              setIsLoggingOut(true);
              logout();
              setToken(null);
              setUser(null);
              setIsLoggingOut(false);
            }
          }
        };
        syncAuth();
      }
    }
  }, [loading, token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}auth/login`, { email, password });
      const { access_token, role } = response.data;
      console.log("Login successful:", { email, role, token: access_token });
      localStorage.setItem("token", access_token);
      localStorage.setItem("role", role || "user");
      setToken(access_token);
      const profileResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}auth/user/profile`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      setUser(profileResponse.data);
      return true;
    } catch (error) {
      console.error("Login failed:", error.response?.data?.detail || error.message);
      throw new Error(error.response?.data?.detail || "Login failed");
    }
  };

  const logoutHandler = () => {
    if (isLoggingOut) return;
    console.log("AuthContext: Logging out");
    setIsLoggingOut(true);
    logout();
    setUser(null);
    setToken(null);
    setIsLoggingOut(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout: logoutHandler, loading, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};